import React, { useState, useEffect } from 'react';
import { TextField, MenuItem, InputAdornment } from '@material-ui/core';
import apiCNP from '../services/apiCNP';
import LoadingShort from '../load/loadingShort';

type LocationOption = {
  _id: string;
  designacao: string;
};

interface LocationSelectProps {
  label: string;
  value: string;
  onChange: (value: string, item?: LocationOption) => void;
  error?: boolean;
  helperText?: string;
  className?: string;
  disabled?: boolean;
  provinciaId?: string;
  required?: boolean;
  returnId?: boolean; // If true, returns _id instead of designacao
}

const cache: {
  provincias: LocationOption[];
  municipios: Record<string, LocationOption[]>;
} = {
  provincias: [],
  municipios: {}
};

const LocationSelect: React.FC<LocationSelectProps> = ({
  label,
  value,
  onChange,
  error,
  helperText,
  className,
  disabled,
  provinciaId,
  required = false,
  returnId = false
}) => {
  const [options, setOptions] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const isMunicipioSelect = !!provinciaId;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        if (isMunicipioSelect) {
          // Já temos os municípios em cache
          if (cache.municipios[provinciaId!]) {
            setOptions(cache.municipios[provinciaId!]);
            return;
          }

          const response = await apiCNP.get(`/municipios-by-provincia/${provinciaId}`);
          const municipios = response.data.municipios || [];
          cache.municipios[provinciaId!] = municipios;
          setOptions(municipios);
        } else {
          // Já temos as províncias em cache
          if (cache.provincias.length > 0) {
            setOptions(cache.provincias);
            return;
          }

          const response = await apiCNP.get('/provincias');
          const provincias = response.data.provincias || [];
          cache.provincias = provincias;
          setOptions(provincias);
        }
      } catch (error) {
        console.error(`Erro ao carregar ${isMunicipioSelect ? 'municípios' : 'províncias'}:`, error);
        setErrorMessage(`Falha ao carregar ${isMunicipioSelect ? 'municípios' : 'províncias'}`);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    // Só busca se:
    // - For select de província OU
    // - For select de município E tiver provinciaId válido
    if (!isMunicipioSelect || (isMunicipioSelect && provinciaId)) {
      fetchData();
    }
  }, [provinciaId, isMunicipioSelect]);

  return (
    <TextField
      select
      fullWidth
      variant="outlined"
      label={label + (required ? ' *' : '')}
      value={value}
      onChange={(e) => {
        const selectedValue = e.target.value as string;
        const selectedItem = options.find(item => 
          returnId ? item._id === selectedValue : item.designacao === selectedValue
        );
        onChange(selectedValue, selectedItem);
      }}
      error={error || !!errorMessage}
      helperText={errorMessage || helperText}
      className={className}
      disabled={disabled || loading}
      InputProps={{
        endAdornment: loading && (
          <InputAdornment position="end">
            <LoadingShort text="" />
          </InputAdornment>
        ),
      }}
    >
      <MenuItem value="">
        {loading ? 'Carregando...' : 'Selecione'}
      </MenuItem>
      {options.map((item) => (
        <MenuItem key={item._id} value={returnId ? item._id : item.designacao}>
          {item.designacao}
        </MenuItem>
      ))}
    </TextField>
  );
};

export default LocationSelect;
